import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { capitalizeWords } from "../ui/capitalizeWords";
import { ColorRing } from "react-loader-spinner";
import { buildApiUrl } from "../../config.js";

const ImageModal = ({ imageUrl, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white/30 backdrop-blur-lg p-4 rounded-lg relative max-w-4xl w-full max-h-[95vh] overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white bg-red-500 rounded-full p-2"
        >
          X
        </button>
        <div className="flex justify-center items-center">
          <img
            src={imageUrl}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

const ClientItemMaster = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  const userRole = localStorage.getItem("role");

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found. Please login again.");
        toast.error("Authentication token not found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await fetch(buildApiUrl("/api/categories"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("API Response:", data);
      
      if (Array.isArray(data)) {
        setCategories(data);
      } else if (data.categories && Array.isArray(data.categories)) {
        // Handle case where API returns { categories: [...] }
        setCategories(data.categories);
      } else {
        setError("Failed to fetch categories: Invalid response format");
        toast.error("Failed to fetch categories: Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      const errorMessage = error.message || "An error occurred while fetching categories";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredCategories = categories.filter((category) => {
    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    const categoryName = category.categoryName?.toLowerCase() || "";
    const itemName = category.itemName?.toLowerCase() || "";
    const itemCode = category.itemCode?.toLowerCase() || "";
    const unit = category.unit?.toLowerCase() || "";
    const pcsPerCtn = category.pcsPerCtn?.toString().toLowerCase() || "";
    const wtPerCtn = category.wtPerCtn?.toString().toLowerCase() || "";
    const cbmPerCtn = category.cbmPerCtn?.toString().toLowerCase() || "";
    return (
      categoryName.includes(normalizedSearchTerm) ||
      itemName.includes(normalizedSearchTerm) ||
      itemCode.includes(normalizedSearchTerm) ||
      unit.includes(normalizedSearchTerm) ||
      pcsPerCtn.includes(normalizedSearchTerm) ||
      wtPerCtn.includes(normalizedSearchTerm) ||
      cbmPerCtn.includes(normalizedSearchTerm)
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategories.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  };

  return (
    <div className="bg-white">
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:space-x-4 w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search Categories..."
              className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-700"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <td className="p-2 font-normal">#</td>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Item Code
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Category
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Item Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Unit
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Image
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Qty/Ctn
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Wt/Ctn
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    CBM/Ctn
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.length > 0 ? (
                  currentItems.map((category, index) => (
                    <tr
                      key={category._id}
                      className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                    >
                      <td className="px-2 py-0 5">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="px-2 py-0.5 font-semibold">
                        {capitalizeWords(category.itemCode)}
                      </td>
                      <td className="px-2 py-0.5">
                        {capitalizeWords(category.categoryName)}
                      </td>
                      <td className="px-2 py-0.5 font-semibold">
                        {capitalizeWords(category.itemName)}
                      </td>
                      <td className="px-2 py-0.5">{category.unit}</td>
                      <td className="px-2 py-0.5">
                        {category.itemImage ? (
                          category.itemImage.includes("drive.google.com") ? (
                            <img
                              src={`https://drive.google.com/thumbnail?id=${
                                category.itemImage.split("id=")[1]
                              }`}
                              alt="Item"
                              className="size-4 object-cover rounded-full"
                              onClick={() =>
                                handleImageClick(
                                  `https://drive.google.com/thumbnail?id=${
                                    category.itemImage.split("id=")[1]
                                  }`
                                )
                              }
                            />
                          ) : (
                            <img
                              src={category.itemImage}
                              alt="Item"
                              className="size-4 object-cover rounded-full"
                              onClick={() =>
                                handleImageClick(category.itemImage)
                              }
                            />
                          )
                        ) : (
                          <span>No image available</span>
                        )}
                      </td>
                      <td className="px-2 py-0.5">
                        {category.pcsPerCtn || "N/A"}
                      </td>
                      <td className="px-2 py-0.5">
                        {category.wtPerCtn || "N/A"}
                      </td>
                      <td className="px-2 py-0.5">
                        {category.cbmPerCtn || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No categories found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredCategories.length)} of{" "}
            {filteredCategories.length} categories
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {isImageModalOpen && (
        <ImageModal imageUrl={selectedImage} onClose={closeImageModal} />
      )}

      <ToastContainer />
    </div>
  );
};

export default React.memo(ClientItemMaster);
